/**
 * 통합관리자 — AI 설정
 * ---------------------------------------------------------------------------
 * 공급사가 계약한 AI 를 여기서 쥔다. 기업은 이 화면을 볼 수 없다.
 *
 * 이 화면에서 정하는 것
 *   키 · 모델 · **원가** 단가 · 사용 여부 · 한 번에 보낼 글자 수
 *
 * 여기서 정하지 않는 것
 *   · 고객에게 **받는** 금액 — 요금제의 「AI 보고서」 단가에 있다.
 *     내는 돈과 받는 돈을 한 화면에 섞으면 둘을 혼동하게 된다.
 *   · 기업이 어느 엔진을 쓸지 — 기업이 자기 설정에서 고른다.
 *
 * 원가 단가를 모델 옆에 둔 이유: 모델을 바꾸면 단가도 바뀐다. 떨어뜨려
 * 놓으면 모델만 바꾸고 단가를 안 고치는 날이 온다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ApiOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface AiProvider {
  providerCode: string;
  displayName: string;
  model: string | null;
  hasApiKey: boolean;
  inputUsdPerMTok: number | null;
  outputUsdPerMTok: number | null;
  maxInputChars: number;
  isEnabled: boolean;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
  updatedAt: string | null;
}

export default function AiSettingPage() {
  const client = useQueryClient();
  const { message } = AntdApp.useApp();
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const providers = useQuery<AiProvider[], NormalizedError>({
    queryKey: ['admin', 'ai'],
    queryFn: async () => (await apiClient.get<AiProvider[]>('/admin/ai')).data,
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['admin', 'ai'] });

  const openEdit = (row: AiProvider) => {
    setEditing(row.providerCode);
    form.setFieldsValue({
      model: row.model,
      // 키는 받아 오지 못한다. 비워 두면 저장된 값을 그대로 둔다.
      apiKey: '',
      inputUsdPerMTok: row.inputUsdPerMTok,
      outputUsdPerMTok: row.outputUsdPerMTok,
      maxInputChars: row.maxInputChars,
      isEnabled: row.isEnabled,
    });
  };

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values || !editing) return;

    setBusy(true);
    try {
      // 빈 키는 보내지 않는다 — 서버가 "안 보냈으면 그대로" 로 읽는다.
      const body = { ...values };
      if (!body.apiKey) delete body.apiKey;

      await apiClient.patch(`/admin/ai/${editing}`, body);
      message.success('저장했습니다.');
      setEditing(null);
      refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ ok: boolean; message: string }>(
        `/admin/ai/${code}/verify`,
      );
      if (data.ok) message.success(data.message);
      else message.error(data.message);
      refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  const columns: ColumnsType<AiProvider> = [
    {
      title: '엔진',
      dataIndex: 'displayName',
      width: 110,
      render: (v: string, row) => (
        <Space>
          <Text strong>{v}</Text>
          {row.isEnabled ? <Tag color="green">사용</Tag> : <Tag>미사용</Tag>}
        </Space>
      ),
    },
    {
      title: '모델',
      dataIndex: 'model',
      render: (v: string | null) => v ?? <Text type="secondary">미설정</Text>,
    },
    {
      title: 'API 키',
      dataIndex: 'hasApiKey',
      width: 100,
      render: (v: boolean) =>
        v ? <Tag color="blue">설정됨</Tag> : <Tag color="red">없음</Tag>,
    },
    {
      title: (
        <Tooltip title="우리가 내는 돈입니다. 고객에게 받는 금액은 요금제에 있습니다.">
          원가 (100만 토큰당)
        </Tooltip>
      ),
      width: 180,
      render: (_, row) =>
        row.inputUsdPerMTok === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <Text style={{ fontSize: 12 }}>
            입력 ${row.inputUsdPerMTok} / 출력 ${row.outputUsdPerMTok}
          </Text>
        ),
    },
    {
      title: '한 번에 보낼 글자',
      dataIndex: 'maxInputChars',
      width: 130,
      render: (v: number) => `${v.toLocaleString('ko-KR')}자`,
    },
    {
      title: '연결 확인',
      width: 150,
      render: (_, row) =>
        row.lastTestedAt === null ? (
          <Text type="secondary">안 해 봄</Text>
        ) : (
          <Tooltip title={row.lastTestError ?? undefined}>
            <Space direction="vertical" size={0}>
              {row.lastTestOk ? <Tag color="green">정상</Tag> : <Tag color="red">실패</Tag>}
              <Text type="secondary" style={{ fontSize: 11 }}>
                {new Date(row.lastTestedAt).toLocaleString('ko-KR')}
              </Text>
            </Space>
          </Tooltip>
        ),
    },
    {
      title: '',
      width: 170,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            설정
          </Button>
          <Button
            size="small"
            icon={<ThunderboltOutlined />}
            disabled={!row.hasApiKey || !row.isEnabled}
            loading={busy}
            onClick={() => void verify(row.providerCode)}
          >
            연결 확인
          </Button>
        </Space>
      ),
    },
  ];

  const target = providers.data?.find((p) => p.providerCode === editing) ?? null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        icon={<ApiOutlined />}
        message="AI 보고서는 공급사가 계약한 API 로 돌아갑니다"
        description={
          <>
            여기서 정하는 금액은 <b>우리가 내는 원가</b>입니다. 고객에게 받는 금액은{' '}
            <b>[과금 &gt; 요금제]</b> 의 「AI 보고서」 단가에 있습니다.
            <br />
            기업은 이 화면을 볼 수 없고, <b>어느 엔진을 쓸지만</b> 자기 설정에서 고릅니다
            (기본 Claude). 키는 제품으로 내려보내지 않고 호출도 플랫폼이 합니다.
          </>
        }
      />

      {providers.isError && <Alert type="error" showIcon message={errorText(providers.error)} />}

      <Card>
        <Table<AiProvider>
          rowKey="providerCode"
          size="small"
          loading={providers.isLoading}
          dataSource={providers.data ?? []}
          columns={columns}
          pagination={false}
        />
      </Card>

      {target && (
        <Card
          title={`${target.displayName} 설정`}
          extra={
            <Space>
              <Button onClick={() => setEditing(null)}>취소</Button>
              <Button type="primary" loading={busy} onClick={() => void save()}>
                저장
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
            <Form.Item
              name="model"
              label="모델"
              rules={[{ required: true, message: '모델 이름을 적으세요' }]}
              extra={
                target.providerCode === 'claude'
                  ? '예: claude-haiku-5-5 (가장 싼 등급)'
                  : '예: gemini-3.8-flash'
              }
            >
              <Input placeholder="모델 이름" />
            </Form.Item>

            <Form.Item
              name="apiKey"
              label="API 키"
              extra={
                target.hasApiKey
                  ? '이미 저장되어 있습니다. 비워 두면 그대로 둡니다 — 바꿀 때만 적으세요.'
                  : '아직 없습니다. 키를 넣어야 「사용」을 켤 수 있습니다.'
              }
            >
              <Input.Password placeholder={target.hasApiKey ? '(변경할 때만 입력)' : 'API 키'} />
            </Form.Item>

            <Space size="large">
              <Form.Item name="inputUsdPerMTok" label="입력 원가 (USD / 100만 토큰)">
                <InputNumber min={0} step={0.01} style={{ width: 180 }} />
              </Form.Item>
              <Form.Item name="outputUsdPerMTok" label="출력 원가 (USD / 100만 토큰)">
                <InputNumber min={0} step={0.01} style={{ width: 180 }} />
              </Form.Item>
            </Space>

            <Form.Item
              name="maxInputChars"
              label="한 번에 보낼 수 있는 글자 수"
              extra="이 값이 보고서 화면에 그대로 안내됩니다. 넘으면 만들기 전에 막습니다."
            >
              <InputNumber min={1000} max={1000000} step={10000} style={{ width: 220 }} />
            </Form.Item>

            <Form.Item name="isEnabled" label="사용" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              저장한 뒤 <b>[연결 확인]</b> 을 눌러 키가 살아 있는지 보세요. 아주 짧은 질문을
              한 번 보냅니다(몇 원).
            </Paragraph>
          </Form>
        </Card>
      )}
    </Space>
  );
}
